import type { Lane, LaneEvent } from "@/lib/track/lane";
import type { LiveSession } from "../live-session";

/**
 * Diffuse ce qu'une `Lane` reçoit vers une session live.
 *
 * # Pourquoi la lane et non le piano
 *
 * Un `PianoEvent` dit quelle touche bouge ; un `LaneEvent` dit **quand**, en
 * `tick` du transport partagé. C'est la seule des deux grandeurs qui ait un
 * sens chez le partenaire : sa machine n'a pas la même origine de temps, mais
 * elle a la même grille. Tant que le geste part avec un horodatage local, deux
 * pianos ne peuvent pas être alignés autrement qu'au hasard du réseau.
 *
 * # Pas de verrou de réentrance
 *
 * Le pont piano en avait besoin : les notes distantes qu'il appliquait lui
 * revenaient par le même `piano.on`. Ici il n'y en a pas, et ce n'est pas un
 * oubli — `PianoControler` ne laisse entrer dans la lane que ce dont
 * l'origine est `"player"`, donc rien de ce qui vient du réseau n'y arrive.
 * Le filtre est posé une fois, à la source, plutôt que rattrapé par un drapeau
 * chez chaque consommateur.
 *
 * # Ce que ce pont ne fait pas
 *
 * Un seul sens : local → réseau. Le sens retour transite encore par le pont
 * piano, qui enfonce les touches distantes en `"app"` — un état de passage,
 * pas une destination : ces notes n'ont rien à faire dans le jeu du joueur, et
 * elles iront dans une lane à elles.
 */
export class LaneLiveBridge {
  private readonly release: () => void;

  constructor(
    lane: Lane,
    private readonly session: LiveSession,
  ) {
    this.release = lane.on((event) => this.onLaneEvent(event));
  }

  dispose(): void {
    this.release();
  }

  private onLaneEvent(event: LaneEvent): void {
    if (event.type === "noteOn") {
      this.session.send(
        { kind: "noteOn", midi: event.note.toMidi(), velocity: event.velocity },
        event.tick,
        event.at,
      );

      return;
    }

    if (event.type === "noteOff") {
      this.session.send({ kind: "noteOff", midi: event.note.toMidi() }, event.tick, event.at);

      return;
    }

    if (event.type === "sustainOn") {
      this.session.send({ kind: "sustain", down: true }, event.tick, event.at);

      return;
    }

    if (event.type === "sustainOff") {
      this.session.send({ kind: "sustain", down: false }, event.tick, event.at);
    }

    // Le reste — mute, hide, clusterChange — décrit la lane, pas un geste.
  }
}
